import { create } from 'zustand';
import { auth } from '../lib/firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { Organization } from '../types/organization.types';
import { persistTrackingParams } from '../utils/navigation';
import { apolloClient } from '../lib/apolloClient';
import { GET_ORGANIZATION } from '../lib/graphql/queries/organizations';
import { getConfig } from '../config/environment';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface OrganizationState {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  currentUser: User | null;
  setOrganization: (org: Organization | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentUser: (user: User | null) => void;
  fetchOrganization: (_force?: boolean) => Promise<void>;
  initialize: () => () => void;
}

const LEGACY_ORG_CACHE_KEY = 'organizationData';

const getOrganizationCacheKey = (user: User | null = auth.currentUser): string | null => {
  if (!user) return null;

  const projectId = getConfig().firebase.projectId;
  return `${LEGACY_ORG_CACHE_KEY}:${projectId}:${user.uid}`;
};

const parseCachedOrganization = (cachedData: string | null): Organization | null => {
  if (!cachedData) return null;

  try {
    const org = JSON.parse(cachedData);
    if (typeof org.onboardingSetup === 'string') {
      try { org.onboardingSetup = JSON.parse(org.onboardingSetup); } catch { org.onboardingSetup = null; }
    }
    if (typeof org.onboardingData === 'string') {
      try { org.onboardingData = JSON.parse(org.onboardingData); } catch { org.onboardingData = null; }
    }
    return org;
  } catch (e) {
    console.error('Error parsing cached organization data:', e);
    return null;
  }
};

export const clearCachedOrganization = (user: User | null = auth.currentUser): void => {
  const scopedKey = getOrganizationCacheKey(user);
  if (scopedKey) {
    localStorage.removeItem(scopedKey);
  }
  localStorage.removeItem(LEGACY_ORG_CACHE_KEY);
};

export const setCachedOrganization = (org: Organization | { id: string } | null, user: User | null = auth.currentUser): void => {
  const scopedKey = getOrganizationCacheKey(user);
  clearCachedOrganization(user);

  if (!org || !scopedKey) return;

  localStorage.setItem(scopedKey, JSON.stringify(org));
};

// Helper to get cached organization data
const getCachedOrganization = (user: User | null = auth.currentUser): Organization | null => {
  const isResetFlow = sessionStorage.getItem('passwordResetFlow') === 'true';
  if (isResetFlow) {
    return null;
  }

  const scopedKey = getOrganizationCacheKey(user);
  const scopedOrg = parseCachedOrganization(scopedKey ? localStorage.getItem(scopedKey) : null);
  if (scopedOrg) {
    return scopedOrg;
  }

  return null;
};

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
  // Initialize with cached data if available
  organization: getCachedOrganization(),
  // Start with loading: true if there's cached data to prevent flash of wrong content
  // The loading state will be set to false once auth state is confirmed
  loading: !!getCachedOrganization(),
  error: null,
  isAuthenticated: !!auth.currentUser,
  currentUser: auth.currentUser,

  setOrganization: (org) => {
    // Cache organization data when setting
    if (org) {
      setCachedOrganization(org);
    } else {
      clearCachedOrganization();
    }
    set({ organization: org });
  },

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setCurrentUser: (user) => set({ currentUser: user, isAuthenticated: !!user }),

  fetchOrganization: async (_force?: boolean) => {
    const { currentUser, organization: existingOrg } = get();
    if (!currentUser) return;

    // Only show loading spinner on first fetch; subsequent fetches are silent
    if (!existingOrg) set({ loading: true });
    try {
      const cachedOrg = getCachedOrganization();
      const organizationId = cachedOrg?.id;
      if (!organizationId) throw new Error('No organization ID found');

      const { data } = await apolloClient.query<{ organization: Record<string, unknown> }>({
        query: GET_ORGANIZATION,
        variables: { organizationId },
        fetchPolicy: 'network-only',
      });

      const gql = data?.organization;
      if (!gql) throw new Error('Organization not found');

      const orgData: Organization = {
        ...gql,
        id: gql.organizationId as string,
        org_name: gql.orgName as string,
        org_phone: gql.orgPhone as string | undefined,
        active_subscription: gql.activeSubscription as boolean | undefined,
        accepted_terms: gql.acceptedTerms as boolean | undefined,
        assistant_name: gql.assistantName as string | undefined,
        assistant_name_pronunciation: gql.assistantNamePronunciation as string | undefined,
        remi_intro_name: gql.remiIntroName as string | undefined,
        call_settings: typeof gql.callSettings === 'string' ? JSON.parse(gql.callSettings) : gql.callSettings as Organization['call_settings'],
        dns_configured: gql.dnsConfigured as boolean | undefined,
        notification_email: gql.notificationEmail as string | undefined,
        sync_link: gql.syncLink as string | undefined,
        primary_contact: typeof gql.primaryContact === 'string' ? JSON.parse(gql.primaryContact) : gql.primaryContact as Organization['primary_contact'],
        brokerage_name: gql.brokerageName as string | undefined,
        client_owner: gql.clientOwner as string | undefined,
        org_links: typeof gql.orgLinks === 'string' ? JSON.parse(gql.orgLinks) : gql.orgLinks as Organization['org_links'],
        last_initial_sync: gql.lastInitialSync as Organization['last_initial_sync'],
        rollout: typeof gql.rolloutConfig === 'string' ? JSON.parse(gql.rolloutConfig) : gql.rolloutConfig as Organization['rollout'],
        timezone: gql.timezone as string,
        client_address: typeof gql.clientAddress === 'string' ? JSON.parse(gql.clientAddress) : gql.clientAddress as Organization['client_address'],
        email: gql.email as string | undefined,
        settings: typeof gql.settings === 'string' ? JSON.parse(gql.settings) : gql.settings as Organization['settings'],
        onboardingSetup: typeof gql.onboardingSetup === 'string' ? JSON.parse(gql.onboardingSetup) : gql.onboardingSetup as Organization['onboardingSetup'],
        onboardingData: typeof gql.onboardingData === 'string' ? JSON.parse(gql.onboardingData) : gql.onboardingData as Organization['onboardingData'],
        phoneNumbers: typeof gql.phoneNumbers === 'string' ? JSON.parse(gql.phoneNumbers) : gql.phoneNumbers as Organization['phoneNumbers'],
        // fubSetup ships as a JSON string from GQL; parse so consumers can read
        // fields like fubSetup.subdomain directly instead of getting undefined.
        fubSetup: typeof gql.fubSetup === 'string' ? JSON.parse(gql.fubSetup) : gql.fubSetup as Organization['fubSetup'],
        crmConnections: Array.isArray(gql.crmConnections)
          ? (gql.crmConnections as Organization['crmConnections'])
          : undefined,
        // Email config
        ...((gql.emailConfig as Record<string, unknown>) && {
          sendgrid_reply_to_email: (gql.emailConfig as Record<string, unknown>)?.sendgridReplyToEmail as string | undefined,
          sendgrid_reply_to_name: (gql.emailConfig as Record<string, unknown>)?.sendgridReplyToName as string | undefined,
          sendgrid_sender_email: (gql.emailConfig as Record<string, unknown>)?.sendgridSenderEmail as string | undefined,
        }),
      };

      setCachedOrganization(orgData, currentUser);
      set({ organization: orgData, error: null, loading: false, isAuthenticated: true });
    } catch (err) {
      console.error('Error fetching organization:', err);
      // SPEC-1788: only sign out on hard auth/org errors. A transient resolver
      // failure (e.g. a stale Spanner/Redis read during a save race) should not
      // log the user out — that turned an "Update Sender" race into a logout.
      const e = err as {
        graphQLErrors?: Array<{ message?: string; extensions?: { code?: string } }>;
        networkError?: unknown;
        message?: string;
      };
      const gqlErrors = Array.isArray(e.graphQLErrors) ? e.graphQLErrors : [];
      const hasNetworkError = !!e.networkError;
      const AUTH_ERROR_CODES = new Set(['UNAUTHENTICATED', 'FORBIDDEN', 'ORG_NOT_FOUND']);
      // Backend Strawberry permission classes raise these literal messages
      // (see remi-api/app/graphql/permissions.py). Anything else is treated
      // as a transient/resolver error and should not log the user out.
      const AUTH_ERROR_MESSAGE_FRAGMENTS = ['authentication required', 'access denied to this organization'];
      const hasAuthGqlError = gqlErrors.some((gqlErr) => {
        const code = gqlErr?.extensions?.code;
        if (code && AUTH_ERROR_CODES.has(code)) return true;
        const msg = (gqlErr?.message || '').toLowerCase();
        return AUTH_ERROR_MESSAGE_FRAGMENTS.some((fragment) => msg.includes(fragment));
      });
      console.warn('[fetchOrganization] error details:', {
        gqlErrorCount: gqlErrors.length,
        hasNetworkError,
        hasAuthGqlError,
        graphQLErrors: gqlErrors,
        networkError: e.networkError,
        message: e.message,
        willSignOut: hasAuthGqlError && !hasNetworkError,
      });
      if (hasAuthGqlError && !hasNetworkError) {
        await signOut(auth);
        set({
          error: 'You are not associated with any organization. Please contact your administrator.',
          organization: null,
          loading: false,
          isAuthenticated: false,
          currentUser: null,
        });
      } else {
        // Transient resolver/network error — keep existing session, just stop loading.
        set({ loading: false });
      }
    }
  },

  initialize: () => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const store = get();
      if (user) {
        store.setCurrentUser(user);
        const cachedOrg = getCachedOrganization();

        if (sessionStorage.getItem('passwordResetFlow') === 'true') {
          // Password reset flow — stay on reset page, clear org
          store.setOrganization(null);
          store.setLoading(false);
        } else if (cachedOrg?.org_name) {
          // Full org in cache — restore immediately then refresh in background to pick up server changes
          store.setOrganization(cachedOrg);
          store.setLoading(false);
          store.fetchOrganization();
        } else if (cachedOrg?.id) {
          // Have org ID but not full org (e.g. interrupted login) — fetch it
          await store.fetchOrganization();
        } else {
          // No cache — first login. LoginPage drives: validateUser → fetchOrganization
          store.setLoading(false);
        }
      } else {
        store.setCurrentUser(null);
        store.setOrganization(null);
        store.setError(null);
        store.setLoading(false);
        clearCachedOrganization();
      }
    });

    return () => unsubscribe();
  },
}));

// Initialize the store — capture cleanup for HMR disposal
const cleanupAuth = useOrganizationStore.getState().initialize();

// Vite HMR: dispose old Firebase auth listeners when the module reloads,
// preventing duplicate onAuthStateChanged handlers from stacking up.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (typeof cleanupAuth === 'function') cleanupAuth();
  });
}

// Subscribe to store changes and sync tracking params
useOrganizationStore.subscribe((state) => {
  const { organization, currentUser } = state;

  if (organization && currentUser) {
    persistTrackingParams({
      org_id: organization.id,
      org_name: organization.org_name,
      user_id: currentUser.uid,
      user_name: currentUser.displayName || '',
    });
  }
});

// Helper hooks
export const useOrganizationTimezone = () => {
  const organization = useOrganizationStore((state) => state.organization);
  // Force re-render when organization changes by including the entire organization object
  return organization?.timezone || 'UTC';
};

export const useOrganizationAccess = () => {
  const { organization, currentUser, loading, error } = useOrganizationStore();

  const hasAccess = Boolean(
    organization &&
      currentUser?.email &&
      ((organization.users || []) as unknown as { email: string }[])
        .map((u) => u.email.toLowerCase())
        .includes(currentUser.email.toLowerCase())
  );

  return {
    hasAccess,
    loading,
    error,
  };
};

/**
 * Hook to check if flywheel is enabled for the organization
 * Returns true only if settings.flywheel_enabled === true
 * Returns false if settings is undefined or flywheel_enabled is false/undefined
 */
export const useFlywheelEnabled = () => {
  const organization = useOrganizationStore((state) => state.organization);
  return organization?.settings?.flywheel_enabled === true;
};

export const useAskRemiEnabled = (): { enabled: boolean; isLoading: boolean } => {
  const env = getConfig().firebase.projectId;
  const isProd = env === 'heyisaai';
  const orgId = useOrganizationStore((s) => s.organization?.id);
  // Prod gates Ask Remi behind the per-org `ask_remi` feature flag
  // (GET /internal/feature-flags/{org_id}/ask_remi). Staging / UAT keep the
  // legacy always-on behavior so non-prod dev flows aren't broken.
  const { data: flagEnabled, isLoading } = useFeatureFlag(orgId, 'ask_remi', { enabled: isProd });
  if (!isProd) return { enabled: true, isLoading: false };
  return { enabled: flagEnabled === true, isLoading };
};
